import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Brain, Lock, Mail, Eye, EyeOff, Key } from 'lucide-react'
import { useAuthStore } from '../stores/auth'
import toast from 'react-hot-toast'

// Demo API Key for testing - in production, this would come from backend
const DEMO_API_KEY = 'mk_e1a7b45edeb7c1fe5ceef6b31fc75228ae7bf2fa24db207d28e50cd0a9dcde28'
const DEMO_TENANT_ID = 'test-tenant-001'

export default function Login() {
  const navigate = useNavigate()
  const { setApiKeyAuth } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Demo authentication - validate demo credentials
      if (formData.email === 'demo@example.com' && formData.password === '!!PowerPacker99') {
        // Create user and tenant objects for the demo
        const demoUser = {
          id: 'user-001',
          tenantId: DEMO_TENANT_ID,
          email: formData.email,
          role: 'admin' as const,
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
        }

        const demoTenant = {
          id: DEMO_TENANT_ID,
          name: 'Demo Organization',
          slug: 'demo',
          plan: 'pro' as const,
          settings: {
            maxAgentsPerSwarm: 50,
            maxSwarms: 20,
            maxStorageGB: 10,
            allowedModels: ['grok-4-1-fast', 'grok-4', 'grok-3-latest'],
            features: {
              browserRendering: true,
              codeExecution: true,
              customTools: true,
              apiAccess: true,
            },
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        // Use API Key authentication
        setApiKeyAuth(DEMO_API_KEY, DEMO_TENANT_ID, demoUser, demoTenant)
        toast.success('Welcome back!')
        navigate('/')
      } else {
        toast.error('Invalid credentials')
      }
    } catch (error) {
      toast.error('Login failed')
      console.error('Login error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Auto-fill demo credentials
  const fillDemoCredentials = () => {
    setFormData({
      email: 'demo@example.com',
      password: '!!PowerPacker99',
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Brain className="h-12 w-12 text-primary-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          MindCore Swarm
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enterprise Multi-Tenant Agent Platform
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-primary-600 hover:text-primary-500">
                  Forgot password?
                </a>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="spinner h-5 w-5" />
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Demo credentials
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <div className="text-xs text-gray-500 mb-3">
                <p>Email: demo@example.com</p>
                <p>Password: !!PowerPacker99</p>
              </div>
              <button
                type="button"
                onClick={fillDemoCredentials}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors"
              >
                <Key className="h-3 w-3 mr-1" />
                Fill Demo Credentials
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
